import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ChevronDown, Home, LogOut, Menu, X } from "lucide-react";
import logo from "../assets/img/heclogo.png";
import { getUserInfo, getUserRole, logout as authLogout } from "../utils/auth";
import { formatRole, getHomePath, getInitials, getNavigation, isSectionActive } from "./navigation";

/**
 * Application header.
 *
 * Layout contract: the header is position:fixed and is immediately followed by
 * a spacer of the same height (`.app-header-spacer`), so page content always
 * starts below it. The height is shared with the sidebar through the CSS
 * variable `--app-header-h` (see index.css).
 */
export default function TopBarComponent() {
  const location = useLocation();
  const userRole = getUserRole();
  const userInfo = getUserInfo();
  const navigation = getNavigation(userRole);
  const homePath = getHomePath(userRole);

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null); // desktop dropdown key
  const [openMobileGroups, setOpenMobileGroups] = useState({});
  const userMenuRef = useRef(null);
  const navRef = useRef(null);

  const displayName =
    [userInfo?.firstName, userInfo?.lastName].filter(Boolean).join(" ") || userInfo?.email || "Signed in";

  // Close menus on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsUserMenuOpen(false);
    setOpenDropdown(null);
  }, [location.pathname]);

  // Escape key + outside clicks
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") {
        setIsMobileMenuOpen(false);
        setIsUserMenuOpen(false);
        setOpenDropdown(null);
      }
    };
    const onClick = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) setIsUserMenuOpen(false);
      if (navRef.current && !navRef.current.contains(e.target)) setOpenDropdown(null);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onClick);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onClick);
    };
  }, []);

  // Lock body scroll while the mobile drawer is open
  useEffect(() => {
    document.body.style.overflow = isMobileMenuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isMobileMenuOpen]);

  const toggleMobileGroup = (key) => setOpenMobileGroups((g) => ({ ...g, [key]: !g[key] }));

  return (
    <>
      <header className="fixed inset-x-0 top-0 z-50 bg-white border-b border-slate-200 shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
        {/* Government strip (hidden on small screens; height must match --app-header-h) */}
        <div className="hidden md:flex h-8 items-center bg-cyan-900 text-cyan-50">
          <div className="w-full px-4 sm:px-6 lg:px-8 flex items-center justify-between text-[11px] tracking-wide">
            <span className="font-medium uppercase">Republic of Rwanda · Higher Education Council</span>
            <span className="text-cyan-200">Scholarship Management Information System</span>
          </div>
        </div>

        {/* Main bar */}
        <div className="h-16 px-4 sm:px-6 lg:px-8 flex items-center justify-between gap-4">
          {/* Brand */}
          <Link to={homePath} className="flex items-center gap-3 min-w-0 rounded-lg">
            <span className="inline-flex w-10 h-10 items-center justify-center rounded-xl bg-white ring-1 ring-slate-200 shadow-sm flex-shrink-0">
              <img src={logo} alt="HEC" className="w-8 h-8 object-contain" />
            </span>
            <span className="hidden sm:block leading-tight">
              <span className="block text-base font-bold text-cyan-900 tracking-tight">HEC MIS</span>
              <span className="block text-[11px] text-slate-500">Higher Education Council</span>
            </span>
          </Link>

          {/* Desktop navigation */}
          <nav ref={navRef} className="hidden lg:flex items-center gap-1" aria-label="Primary">
            {navigation.map((section) => {
              const Icon = section.icon;
              const active = isSectionActive(section, location.pathname);
              const base =
                "flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg transition-colors whitespace-nowrap";
              const tone = active
                ? "bg-cyan-50 text-cyan-800"
                : "text-slate-600 hover:text-cyan-800 hover:bg-slate-100";

              if (section.path) {
                return (
                  <Link key={section.key} to={section.path} className={`${base} ${tone}`}>
                    <Icon className="w-4 h-4" />
                    <span>{section.title}</span>
                  </Link>
                );
              }

              const isOpen = openDropdown === section.key;
              return (
                <div
                  key={section.key}
                  className="relative"
                  onMouseEnter={() => setOpenDropdown(section.key)}
                  onMouseLeave={() => setOpenDropdown((k) => (k === section.key ? null : k))}
                >
                  <button
                    type="button"
                    aria-haspopup="menu"
                    aria-expanded={isOpen}
                    onClick={() => setOpenDropdown(isOpen ? null : section.key)}
                    className={`${base} ${tone}`}
                  >
                    <Icon className="w-4 h-4" />
                    <span>{section.title}</span>
                    <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                  </button>

                  <div
                    role="menu"
                    className={`absolute left-0 top-full pt-2 w-72 transition-all duration-150 origin-top ${
                      isOpen ? "opacity-100 visible translate-y-0" : "opacity-0 invisible -translate-y-1 pointer-events-none"
                    }`}
                  >
                    <div className="bg-white rounded-xl border border-slate-200 shadow-xl shadow-slate-900/10 py-2 max-h-[calc(100vh-7.5rem)] overflow-y-auto">
                      {section.items.map((item) => {
                        const ItemIcon = item.icon;
                        const itemActive = location.pathname === item.path;
                        return (
                          <Link
                            key={item.path}
                            to={item.path}
                            role="menuitem"
                            className={`flex items-start gap-3 px-4 py-2.5 text-sm transition-colors ${
                              itemActive ? "bg-cyan-50 text-cyan-900" : "text-slate-700 hover:bg-slate-50 hover:text-cyan-800"
                            }`}
                          >
                            <ItemIcon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${itemActive ? "text-cyan-600" : "text-slate-400"}`} />
                            <span className="min-w-0">
                              <span className="block font-medium leading-5">{item.title}</span>
                              {item.description && <span className="block text-xs text-slate-400 leading-4">{item.description}</span>}
                            </span>
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                </div>
              );
            })}
          </nav>

          {/* Right cluster */}
          <div className="flex items-center gap-2 sm:gap-3">
            {userRole && (
              <span className="hidden md:inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-semibold uppercase tracking-wide bg-cyan-50 text-cyan-800 border border-cyan-100">
                {formatRole(userRole)}
              </span>
            )}

            {/* User menu */}
            <div ref={userMenuRef} className="relative">
              <button
                type="button"
                onClick={() => setIsUserMenuOpen((o) => !o)}
                aria-haspopup="menu"
                aria-expanded={isUserMenuOpen}
                className="flex items-center gap-2 pl-1 pr-2 py-1 rounded-full hover:bg-slate-100 transition-colors"
              >
                <span className="inline-flex w-8 h-8 items-center justify-center rounded-full bg-gradient-to-br from-cyan-500 to-cyan-700 text-white text-xs font-bold">
                  {getInitials(userInfo)}
                </span>
                <ChevronDown className={`w-4 h-4 text-slate-500 transition-transform ${isUserMenuOpen ? "rotate-180" : ""}`} />
              </button>

              {isUserMenuOpen && (
                <div role="menu" className="absolute right-0 top-full mt-2 w-64 bg-white rounded-xl border border-slate-200 shadow-xl shadow-slate-900/10 overflow-hidden">
                  <div className="px-4 py-3 border-b border-slate-100 bg-slate-50/60">
                    <p className="text-sm font-semibold text-slate-900 truncate">{displayName}</p>
                    {userInfo?.email && <p className="text-xs text-slate-500 truncate">{userInfo.email}</p>}
                    {userRole && <p className="mt-1 text-[11px] font-medium uppercase tracking-wide text-cyan-700">{formatRole(userRole)}</p>}
                  </div>
                  <div className="py-1">
                    <Link
                      to={homePath}
                      role="menuitem"
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 hover:text-cyan-800 transition-colors"
                    >
                      <Home className="w-4 h-4 text-slate-400" />
                      <span>{homePath === "/dashboard" ? "Dashboard" : "Home"}</span>
                    </Link>
                  </div>
                  <div className="border-t border-slate-100 py-1">
                    <button
                      type="button"
                      role="menuitem"
                      onClick={() => authLogout()}
                      className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors"
                    >
                      <LogOut className="w-4 h-4" />
                      <span>Sign Out</span>
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Mobile menu toggle */}
            <button
              type="button"
              onClick={() => setIsMobileMenuOpen((o) => !o)}
              className="lg:hidden p-2 rounded-lg text-slate-700 hover:bg-slate-100 transition-colors"
              aria-label="Toggle navigation menu"
              aria-expanded={isMobileMenuOpen}
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </header>

      {/* Reserves the header's height in normal flow */}
      <div className="app-header-spacer" aria-hidden="true" />

      {/* Mobile overlay */}
      {isMobileMenuOpen && (
        <div className="fixed inset-0 z-[55] bg-slate-900/50 lg:hidden" onClick={() => setIsMobileMenuOpen(false)} />
      )}

      {/* Mobile drawer */}
      <div
        className={`fixed top-0 right-0 z-[60] w-80 max-w-[85vw] h-full bg-white shadow-2xl flex flex-col transform transition-transform duration-300 ease-in-out lg:hidden ${
          isMobileMenuOpen ? "translate-x-0" : "translate-x-full"
        }`}
        aria-hidden={!isMobileMenuOpen}
      >
        <div className="flex items-center justify-between px-5 h-16 border-b border-slate-200">
          <div className="flex items-center gap-3">
            <img src={logo} alt="HEC" className="w-8 h-8 object-contain" />
            <div className="leading-tight">
              <p className="text-sm font-bold text-cyan-900">HEC MIS</p>
              <p className="text-[11px] text-slate-500">Higher Education Council</p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setIsMobileMenuOpen(false)}
            className="p-2 rounded-lg hover:bg-slate-100 transition-colors"
            aria-label="Close menu"
          >
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-3">
          <span className="inline-flex w-10 h-10 items-center justify-center rounded-full bg-gradient-to-br from-cyan-500 to-cyan-700 text-white text-sm font-bold">
            {getInitials(userInfo)}
          </span>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-slate-900 truncate">{displayName}</p>
            {userRole && <p className="text-[11px] font-medium uppercase tracking-wide text-cyan-700">{formatRole(userRole)}</p>}
          </div>
        </div>

        <nav className="flex-1 overflow-y-auto px-3 py-3 space-y-1" aria-label="Mobile">
          {navigation.map((section) => {
            const Icon = section.icon;
            if (section.path) {
              const active = location.pathname === section.path;
              return (
                <Link
                  key={section.key}
                  to={section.path}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                    active ? "bg-cyan-600 text-white" : "text-slate-700 hover:bg-slate-100"
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  <span>{section.title}</span>
                </Link>
              );
            }
            const open = openMobileGroups[section.key] ?? isSectionActive(section, location.pathname);
            return (
              <div key={section.key}>
                <button
                  type="button"
                  onClick={() => toggleMobileGroup(section.key)}
                  aria-expanded={open}
                  className="w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-sm font-semibold text-slate-800 hover:bg-slate-100 transition-colors"
                >
                  <span className="flex items-center gap-3">
                    <Icon className="w-5 h-5 text-cyan-600" />
                    {section.title}
                  </span>
                  <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
                </button>
                {open && (
                  <div className="ml-4 pl-3 border-l border-slate-200 mt-1 mb-2 space-y-0.5">
                    {section.items.map((item) => {
                      const ItemIcon = item.icon;
                      const active = location.pathname === item.path;
                      return (
                        <Link
                          key={item.path}
                          to={item.path}
                          className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
                            active ? "bg-cyan-600 text-white" : "text-slate-600 hover:bg-slate-100 hover:text-cyan-800"
                          }`}
                        >
                          <ItemIcon className={`w-4 h-4 ${active ? "text-white" : "text-slate-400"}`} />
                          <span>{item.title}</span>
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        <div className="p-4 border-t border-slate-200">
          <button
            type="button"
            onClick={() => authLogout()}
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-red-600 text-white text-sm font-medium hover:bg-red-700 transition-colors"
          >
            <LogOut className="w-4 h-4" />
            Sign Out
          </button>
        </div>
      </div>
    </>
  );
}
